import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { Box, Tooltip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Flex from "@/components/Flex";
import PageLayout from "@/components/PageLayout";
import Text from "@/components/Text";
import ActionMenu from "@/components/app/ActionMenu";
import { BILLING_TABLE_ROW_HEIGHT } from "@/components/app/provider/patient/chart";
import { PROVIDER_PATHS } from "@/constants/paths";
import { useSuperBillListV2 } from "@/queries/billings/use-superBill-list-v2";
import { formatDate, formatDateTime } from "@/utils/date-time-utility";
import { PatientName } from "@/utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  formatMoney,
  getFormattedId,
  getXtenantId,
  // hasActiveFilters,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "@/utils/utils";
import { getInvoiceActionOptions } from "./billing-utils";
import InvoiceStatusChip from "./common-components/InvoiceStatusChip";
import { useInvoiceRowActions } from "./hooks/use-invoice-row-actions";

type OutletContextType = {
  filter: any;
  setFilter: (v: any) => void;
  searchKey: string;
  rowSelectionModel: import("@mui/x-data-grid").GridRowSelectionModel;
  setRowSelectionModel: (
    v: import("@mui/x-data-grid").GridRowSelectionModel
  ) => void;
};

const SuperBill = () => {
  const {
    filter,
    // setFilter,
    searchKey: searchInputKey,
    rowSelectionModel,
    setRowSelectionModel,
  } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const {
    data: superBillList,
    isLoading,
    isFetching,
    refetch,
  } = useSuperBillListV2({
    pagination: pagination,
    gridSorting: gridSorting,
    searchKey: searchInputKey ?? undefined,
    encounterId: filter?.visit_Id ? filter?.visit_Id : undefined,
    xTENANTID: getXtenantId(),
    billStatus: filter?.bill_Status?.value ?? undefined,
    providerId: filter?.provider_Name?.value ?? undefined,
    archive: filter?.archive ?? false,
    dateRange: filter?.date_range ?? undefined,
    startDate:
      filter?.date_range === "CUSTOM_RANGE"
        ? filter?.startDate
          ? dayjs(filter?.startDate).toISOString()
          : undefined
        : undefined,
    endDate:
      filter?.date_range === "CUSTOM_RANGE"
        ? filter?.endDate
          ? dayjs(filter?.endDate).endOf("day").toISOString()
          : undefined
        : undefined,
  });

  // Row actions (menu handlers + their modals/drawers) are shared with the
  // Patient Chart → Financial invoice list so both offer the same actions.
  const { handleInvoiceAction, invoiceActionModals, invoiceActionPermissions } =
    useInvoiceRowActions({ onRefetch: refetch });

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "SuperBillID",
        flex: 1,
        minWidth: 200,
        headerName: "Invoice No & Date",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Flex gap={"10px"}>
              <Box>
                <Flex gap={"6px"} alignItems={"center"}>
                  <Text
                    isTabletext
                    fontWeight={"medium"}
                    color={"#004186"}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {`${row?.uuid ? getFormattedId(row?.uuid) : "-"}`}
                  </Text>
                  {row?.invoiceSentToPatient && (
                    <Tooltip title="Invoice sent to Patient">
                      <MarkEmailReadIcon
                        sx={{ color: "#00B917", fontSize: "16px" }}
                      />
                    </Tooltip>
                  )}
                </Flex>
                <Text isTabletext sx={{ color: "#1A1A1A99", lineHeight: 1.2 }}>
                  {`${formatDate(row?.billDate ?? "")}`}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      {
        field: "encounterId",
        flex: 0.6,
        minWidth: 90,
        headerName: "Visit ID",
        sortable: false,
        renderCell: ({ row }) => (
          <Text isTabletext>
            {row?.encounterId ? getFormattedId(row?.encounterId) : "-"}
          </Text>
        ),
      },
      {
        field: "claimId",
        flex: 0.6,
        minWidth: 90,
        headerName: "Claim ID",
        sortable: false,
        renderCell: ({ row }) =>
          row?.claimId ? (
            <Text
              isTabletext
              color={"#004186"}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() =>
                navigate(
                  PROVIDER_PATHS.BILLING +
                    `claims/edit-claim?patientId=${row?.patientId}&claimId=${row?.claimId}&readOnly=true`
                )
              }
            >
              {getFormattedId(row?.claimId)}
            </Text>
          ) : (
            <Text isTabletext>-</Text>
          ),
      },
      {
        field: "appointmentDate",
        flex: 1,
        minWidth: 150,
        headerName: "Date of Service",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Flex gap={"10px"}>
              <Box>
                {/* <Text
                  isTabletext
                  fontWeight={"medium"}
                  color={"#004186"}
                  sx={{ lineHeight: 1.2 }}
                >
                  {`${isEmpty(row?.encounterId)
                    ? "-"
                    : getFormattedId(row?.encounterId)
                    }`}
                </Text>
                  {`${
                    isEmpty(row?.encounterId)
                      ? "-"
                      : getFormattedId(row?.encounterId)
                  }`}
                </Text> */}
                <Text isTabletext sx={{ lineHeight: 1.2 }}>
                  {`${
                    row?.encounterDate
                      ? formatDateTime(row?.encounterDate)
                      : row?.appointmentDate
                        ? formatDateTime(row?.appointmentDate)
                        : row?.dateOfService
                          ? formatDateTime(row?.dateOfService)
                          : ""
                  }`}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      {
        field: "patientName",
        flex: 0.6,
        minWidth: 180,
        disableColumnMenu: true,
        headerName: "Patient Name",

        renderCell: ({ row }) => (
          <PatientName
            name={row?.patientName ?? ""}
            gender={row?.patientGender ?? ""}
            patientId={row?.patientId ?? ""}
            birthDate={row?.patientDOB ?? ""}
            shortenGender={false}
          />
        ),
      },
      {
        field: "renderingProvider",
        flex: 1,
        minWidth: 150,
        headerName: "Provider",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Flex gap={"10px"} sx={{}}>
              <Box>
                <Text isTabletext marginBottom={"1px"}>
                  {row?.renderingProviderName ?? ""}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      // {
      //   field: "serviceState",
      //   flex: 0.6,
      //   minWidth: 150,
      //   headerName: "Service Location",
      //   sortable: false,
      //   renderCell: ({ row }) => (
      //     <RenderWithTooltip
      //       value={
      //         row?.serviceState
      //           ? STATE_RECORD_FULL_NAME_MAPPER[row.serviceState]
      //           : ""
      //       }
      //
      //     />
      //   ),
      // },
      // {
      //   field: "placeOfService",
      //   flex: 1,
      //   minWidth: 150,
      //   headerName: "Place Of Service",
      //   sortable: false,
      //   renderCell: ({ value }) => (
      //     // SUPERBILLPLACEOFSERVICEMAPPER[value] || "-",
      //     <RenderWithTooltip
      //       value={SUPERBILLPLACEOFSERVICEMAPPER[value]}
      //
      //     />
      //   ),
      // },
      {
        field: "subTotal",
        flex: 0.5,
        minWidth: 100,
        headerName: "Invoice Amount",
        sortable: false,
        renderCell: ({ value }) => formatMoney(value),
      },
      {
        field: "prevPaid",
        flex: 0.5,
        minWidth: 100,
        headerName: "Paid Amount",
        sortable: false,
        renderCell: ({ value }) => formatMoney(value),
      },
      // {
      //   field: "insuranceAmount",
      //   flex: 0.5,
      //   minWidth: 100,
      //   headerName: "Claim Amt.",
      //   sortable: false,
      //   renderCell: ({ value }) => (value != null ? `$ ${value}` : "-"),
      // },
      // {
      //   field: "modified",
      //   headerName: "Last Modified",
      //   flex: 0.6,
      //   width: 150,
      //   renderCell: ({ value }) => (
      //     <RenderWithTooltip value={formatDateTime(value)} />
      //   ),
      // },
      {
        field: "patientBalance",
        headerName: "Patient Balance",
        flex: 0.6,
        minWidth: 100,
        sortable: false,
        renderCell: ({ value }) => formatMoney(value),
      },
      // billDate
      {
        field: "dueDate",
        headerName: "Due Date",
        flex: 0.5,
        minWidth: 100,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "invoiceType",
        headerName: "Invoice Type",
        flex: 0.5,
        minWidth: 100,
        renderCell: ({ value }) =>
          value === "PR"
            ? "PR"
            : capitalize(replaceUnderScoreAddSpace(value)) || "-",
      },
      {
        //pib
        field: "superBillStatus",
        headerName: "Status",
        flex: 0.5,
        minWidth: 95,
        renderCell: ({ value, row }) => {
          // Show the applied discount on hover for a Paid invoice that was
          // settled by a discount (e.g. fully-discounted). (ECRH-5380 - Issue 8)
          const discountTooltip =
            value === "PAID" && Number(row?.discount) > 0
              ? `Discount Applied = ${
                  row?.discountType === "PERCENTAGE"
                    ? `${row?.discount}%`
                    : `$${Number(row?.discount).toFixed(2)}`
                }`
              : "";
          const tooltipTitle =
            value === "CANCELLED" ? row?.cancelReason : discountTooltip;
          return <InvoiceStatusChip status={value} tooltip={tooltipTitle} />;
        },
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                // ECRH-4979: dynamic action options based on invoice payment status.
                // Original static list kept below for reference.
                // list: [
                //   { value: "ViewBill", label: "View Details" },
                //   {
                //     value: "modify",
                //     label: "Edit Invoice",
                //     isDisabled: row?.superBillStatus === "PAID",
                //     hoverMsg: "Bill is already Paid",
                //   },
                //   {
                //     value: "createClaim",
                //     label: "Create Claim",
                //     isDisabled:
                //       row?.paymentMode === "ONLINE" ||
                //       isEmpty(row?.encounterId),
                //     hoverMsg:
                //       row?.paymentMode === "ONLINE"
                //         ? "Claims are not applicable for Self-Pay"
                //         : "Visit is not present for this Invoice",
                //   },
                //   // { value: "voidPayment", label: "Void Payment" },
                //   // {
                //   //   value: "archive",
                //   //   label: row?.archive ? "Unarchive" : "Archive",
                //   // },
                //   {
                //     value: "sendInvoiceViaEmail",
                //     label: "Send Invoice via Email (Payment Link)",
                //   },
                //   {
                //     value: "sendInvoiceViaEmailWithoutPaymentLink",
                //     label: "Send Invoice via Email (Without Payment Link)",
                //   },
                //   {
                //     value: "cancel",
                //     label: "Cancel",
                //     isDisabled: row?.superBillStatus === "CANCELLED",
                //     hoverMsg: "Bill is already Cancelled",
                //   },
                //   {
                //     value: "printAsCMS1500Form",
                //     label: "Print As CMS 1500 Form",
                //     isDisabled: row?.paymentMode === "ONLINE",
                //     hoverMsg: "Not applicable for Self-Pay",
                //   },
                //   {
                //     value: "collectPayment",
                //     label: "Collect Payment",
                //     isDisabled: row?.superBillStatus === "PAID",
                //     hoverMsg: "Bill is already Paid",
                //   },
                // ],
                list: getInvoiceActionOptions(row),
                compWith: invoiceActionPermissions,
              })}
              onActionClick={(v) => handleInvoiceAction(v, row)}
            />
          );
        },
      },
    ],
    [
      "patientName",
      "appointmentDate",
      "subTotal",
      "insuranceAmount",
      "patientBalance",
    ]
  );

  return (
    <PageLayout>
      {invoiceActionModals}

      {/* <ActiveFilters filters={filter} setFilters={setSuperBillFilter} /> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={superBillList?.data?.content ?? []}
        columns={COLUMNS}
        rowCount={superBillList?.data?.page?.totalElements ?? 10}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        loading={isLoading || isFetching}
        sortModel={gridSorting}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(rowSelected) => {
          setRowSelectionModel(rowSelected);
        }}
        keepNonExistentRowsSelected
        rowHeight={BILLING_TABLE_ROW_HEIGHT}
      />
    </PageLayout>
  );
};

export default SuperBill;

// Moved to SUPER_BILL_STATUS_VARIANT_MAP in billing-constant.ts so Billing and
// the Patient Chart share one status/colour mapping.
// const SuperBillStatusVariantMap: Record<
//   string,
//   "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending"
// > = {
//   ACCEPTED: "success",
//   DRAFT: "warning",
//   REJECTED: "error",
//   SUBMITTED: "info",
//   DENIED: "error",
//   PAID: "success",
//   CANCELLED: "error",
//   PENDING: "pending",
//   UNPAID: "pending",
//   OVERDUE: "warning",
// };
